import type { ReactElement } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import type { ActiveRoute, Meta, RouteState } from "@/__main__/router.mjs";
import router, {
  EVENT_CHANGE_ROUTE,
  EVENT_CHANGE_ROUTE_COMPONENT,
  EVENT_LOADED_DATA,
  EVENT_LOADED_META,
  EVENT_LOADING_DATA,
  updateRoute,
} from "@/__main__/router.mjs";
import deepEqual, { shallowEqual } from "@/util/deep-equal.mjs";
import { devError } from "@/util/dev.mjs";

export function useRouteComponent() {
  const [rc, setRc] = useState(router.RouteComponent);

  useEffect(() => {
    function changeListener(newRC: () => ReactElement) {
      setRc(newRC);
    }

    router.events.on(EVENT_CHANGE_ROUTE_COMPONENT, changeListener);

    return () => {
      router.events.off(EVENT_CHANGE_ROUTE_COMPONENT, changeListener);
    };
  }, []);

  return rc;
}

// use route metadata on the current route
// named metadata instead of meta to discern between the meta fn and the returned value (metadata)
export function useRouteMetadata() {
  const [metadata, setMetadata] = useState<Partial<Meta>>(
    router.route?.metadata ?? {},
  );

  useEffect(() => {
    const metaListener = () => {
      const newMetadata = router.route?.metadata ?? {};
      if (!newMetadata || shallowEqual(metadata, newMetadata)) return;
      setMetadata(newMetadata);
    };

    router.events.on(EVENT_LOADED_META, metaListener);

    return () => {
      router.events.off(EVENT_LOADED_META, metaListener);
    };
  }, [metadata]);

  return metadata;
}

type CompareFn = (prev: ActiveRoute | null, next: ActiveRoute) => boolean;
type UseRouteOptions = {
  isGlobal?: boolean;
};
/**
 * Returns the active route, however it *will not* be updated if the route component has changed.
 * This is useful for components that need to know or use the current route's information, but
 * should not react when navigating away from the current page.
 *
 * This accepts an optional `compareFn` mainly used to suppress updates to prevent unnecessary re-renders.
 */
export function useRoute<
  S extends Record<PrimitiveKey, unknown> = Record<PrimitiveKey, unknown>,
>(...options: (CompareFn | UseRouteOptions)[]): ActiveRoute<S> {
  // Although the nullish check is kinda bad here because it gives the
  // impression that a route is set while it isn't, it's only used here
  // because the common use case is something like:
  // const { params } = useRoute();
  //
  // This would unexpectedly explode without this null fallback.
  const route: ActiveRoute = router.route ?? {
    state: {} as RouteState,
    path: "",
    currentPath: "",
    searchParams: new URLSearchParams(),
    parameters: [],
  };

  const [compareFn, opts] = useMemo(
    () =>
      options.reduce(
        (acc, cur) => {
          if (typeof cur !== "function") {
            Object.assign(acc[1], cur);
            return acc;
          }
          if (acc[0]) devError("[useRoute] compareFn already set", { route });
          acc[0] = cur;
          return acc;
        },
        [undefined, {}] as [CompareFn | undefined, { isGlobal?: boolean }],
      ),
    options,
  );

  const [currentRoute, setCurrentRoute] = useState(route);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;

    function changeListener(newRoute: ActiveRoute) {
      // This should not run on an unmounted component, nor on a completely
      // different route component.
      if (
        !mountedRef.current ||
        (!opts.isGlobal &&
          router.previousRoute?.component !== newRoute?.component)
      )
        return;

      let shouldUpdate;
      if (compareFn) {
        shouldUpdate = !compareFn(router.previousRoute, newRoute);
      } else {
        shouldUpdate =
          router.previousRoute?.currentPath !== newRoute?.currentPath ||
          router.previousRoute?.searchParams?.toString() !==
            newRoute.searchParams?.toString() ||
          !deepEqual(
            { ...router.previousRoute?.state, transient: {} },
            { ...newRoute?.state, transient: {} },
          );
      }

      if (shouldUpdate) {
        setCurrentRoute(newRoute);
      }
    }

    router.events.on(EVENT_CHANGE_ROUTE, changeListener);

    return () => {
      mountedRef.current = false;
      router.events.off(EVENT_CHANGE_ROUTE, changeListener);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return currentRoute as ActiveRoute<S>;
}

// This is a version of useRoute that doesn't trigger re-render.
export function useTransientRoute<
  S extends Record<PrimitiveKey, unknown> = Record<PrimitiveKey, unknown>,
>(): ActiveRoute<S> {
  return Object.assign({}, router.route) as ActiveRoute<S>;
}

/**
 * The `useIsLoaded` hook should be used only in conjunction with
 * checking if a path in the readState exists.
 *
 * @param {boolean} isGlobal - Whether it should be path dependent.
 * @returns {boolean} Is the route loaded?
 */
export function useIsLoaded(isGlobal = false) {
  const [isLoaded, setIsLoaded] = useState<boolean>(router.isLoaded);

  useEffect(() => {
    // Only trigger this hook for same route. This is to prevent
    // edge cases when a new route is loading while the old route
    // also gets the loading update.
    const initialPath = router.route?.currentPath;

    // Edge case for initialization.
    setIsLoaded(router.isLoaded);

    function onLoadingData(route: ActiveRoute) {
      if (route?.currentPath !== initialPath && !isGlobal) {
        return;
      }

      setIsLoaded(false);
    }

    function onLoadedData(route: ActiveRoute) {
      if (route?.currentPath !== initialPath && !isGlobal) {
        return;
      }

      setIsLoaded(true);
    }

    router.events.on(EVENT_LOADING_DATA, onLoadingData);
    router.events.on(EVENT_LOADED_DATA, onLoadedData);

    return () => {
      router.events.off(EVENT_LOADING_DATA, onLoadingData);
      router.events.off(EVENT_LOADED_DATA, onLoadedData);
    };
  }, [isGlobal]);

  return isLoaded;
}

// similar to useState, but the value is set to the query param
export function useQuery<T>(
  key: string,
  defaultValue?: T,
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    const params = router.route?.searchParams;
    if (!params) return defaultValue;
    const param = params.get(key);
    if (param === null) return defaultValue;
    return param as unknown as T;
  });

  useEffect(() => {
    function changeListener() {
      const params = router.route?.searchParams;
      if (!params) return;
      const param = params.get(key);
      if (param === null) {
        setValue(defaultValue);
      } else {
        setValue(param as unknown as T);
      }
    }

    router.events.on(EVENT_CHANGE_ROUTE, changeListener);

    return () => {
      router.events.off(EVENT_CHANGE_ROUTE, changeListener);
    };
  }, [defaultValue, key]);

  const setQuery = (newValue: T) => {
    const params = router.route?.searchParams;
    if (!params) return;
    if (
      newValue !== undefined &&
      newValue !== null &&
      newValue !== defaultValue
    ) {
      params.set(key, newValue as unknown as string);
    } else {
      params.delete(key);
    }
    updateRoute(router.route.currentPath, params);
  };

  return [value, setQuery];
}

export function useQueryToggle(key: string, defaultValue: boolean = false) {
  const [value, setValue] = useQuery<string>(key, defaultValue ? "1" : "0");

  const setQuery = (newValue: boolean) => setValue(newValue ? "1" : "0");

  const returnVal = value === "1";
  return [returnVal, () => setQuery(!returnVal), setQuery] as const;
}

export function useQueryValues<
  T extends readonly string[],
  K extends T[number],
>(keys: T): Record<K, string>;
export function useQueryValues<T extends string[], K extends T[number]>(
  keys: T,
): Record<K, string> {
  const [values, setValues] = useState<Record<K, string>>(() => {
    const params = router.route?.searchParams;
    if (!params) return {} as Record<K, string>;
    const values = {} as Record<K, string>;
    for (const key of keys) values[key] = params.get(key);
    return values;
  });

  useEffect(() => {
    function changeListener() {
      const params = router.route?.searchParams;
      if (!params) return;
      const values = {} as Record<K, string>;
      for (const key of keys) values[key] = params.get(key);
      setValues(values);
    }

    router.events.on(EVENT_CHANGE_ROUTE, changeListener);

    return () => {
      router.events.off(EVENT_CHANGE_ROUTE, changeListener);
    };
  }, [keys]);

  return values;
}
